import { formatMoney } from '../utils/currencyUtil';
import { formatPlanDuration } from '../utils/subscriptionDateUtil';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * One subscription plan, laid out as a pricing card: name, what it is for,
 * the price and length, and what it includes.
 *
 * Used in three places — the admin's plan list, the client's Subscribe dialog
 * and nowhere else that needs a different look — so the plan reads the same
 * to the person selling it and the person buying it.
 *
 * @param {object} props
 * @param {object} props.plan - a normalised plan (see posSubscriptionUtil)
 * @param {object} props.config - for the currency symbol
 * @param {boolean} [props.selected] - outlines the card as the current choice
 * @param {() => void} [props.onSelect] - when given, a select button is shown
 * @param {string} [props.selectLabel] - text on that button
 * @param {boolean} [props.isCurrent] - marks the plan the client is on now
 * @param {boolean} [props.showAdminBadges] - shows "Hidden from clients"
 * @param {React.ReactNode} [props.children] - extra actions (admin Edit/Remove)
 * @returns {JSX.Element}
 */
function PosPlanCard({
  plan,
  config,
  selected = false,
  onSelect,
  selectLabel = 'Select',
  isCurrent = false,
  showAdminBadges = false,
  children,
}) {
  const badges = [];
  if (isCurrent) badges.push({ key: 'current', label: 'Current plan', tone: 'current' });
  if (plan.isTrial) badges.push({ key: 'trial', label: 'Free trial', tone: 'trial' });
  if (plan.recommended) badges.push({ key: 'rec', label: 'Recommended', tone: 'rec' });
  if (plan.isCustom) badges.push({ key: 'custom', label: 'Custom pricing', tone: 'muted' });
  if (showAdminBadges && !plan.visibleToClients) badges.push({ key: 'hidden', label: 'Hidden from clients', tone: 'muted' });

  return (
    <article className={`ps-plan${plan.recommended ? ' ps-plan-rec' : ''}${selected ? ' ps-plan-selected' : ''}${isCurrent ? ' ps-plan-current' : ''}`}>
      <header className="ps-plan-head">
        <h3 className="ps-plan-name">{plan.name}</h3>
        {badges.length > 0 && (
          <div className="ps-plan-badges">
            {badges.map((badge) => (
              <span key={badge.key} className={`ps-badge ps-badge-${badge.tone}`}>{badge.label}</span>
            ))}
          </div>
        )}
      </header>

      {plan.description && <p className="ps-plan-desc">{plan.description}</p>}

      <div className="ps-plan-price">
        {plan.isCustom ? (
          <span className="ps-plan-amount">By quote</span>
        ) : (
          <>
            <span className="ps-plan-amount">{plan.isTrial || plan.price === 0 ? 'Free' : formatMoney(plan.price, config)}</span>
            {plan.compareAtPrice > plan.price && <s className="ps-plan-was">{formatMoney(plan.compareAtPrice, config)}</s>}
            <span className="ps-plan-per">/ {formatPlanDuration(plan.durationValue, plan.durationUnit)}</span>
          </>
        )}
      </div>

      {onSelect && (
        <button
          type="button"
          className={`pa-btn ${selected || plan.recommended ? 'pa-btn-primary' : 'pa-btn-quiet'} ps-plan-select`}
          onClick={onSelect}
          aria-pressed={selected}
        >
          {selected ? 'Selected' : selectLabel}
        </button>
      )}

      {plan.features.length > 0 && (
        <ul className="ps-plan-features">
          {plan.features.map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${line}-${index}`}>{line}</li>
          ))}
        </ul>
      )}

      {children && <div className="ps-plan-actions">{children}</div>}
    </article>
  );
}

export default PosPlanCard;
